import { repoHostFromCloneUrl, type GroomingRepoHost } from "@ttc-spectree/toolbox-contract";

/**
 * Where a mapped repository's code is hosted, for host-aware wording (epic
 * PI-SPEC-SW-E318, feature PI-SPEC-SW-1679). The config read's `host` wins;
 * an entry without one (every ADO entry, or a config read from an older
 * SpecTree) is classified from its `cloneUrl`; with neither it is ADO.
 */

/** A mapped repository as the config read carries it. */
export interface RepoHostSource {
  project?: string | undefined;
  repo: string;
  host?: GroomingRepoHost | undefined;
  cloneUrl?: string | undefined;
}

/** The host of one mapped repository. Pure. */
export function repoHostOf(entry: Pick<RepoHostSource, "host" | "cloneUrl">): GroomingRepoHost {
  if (entry.host === "github" || entry.host === "ado") return entry.host;
  return typeof entry.cloneUrl === "string" ? repoHostFromCloneUrl(entry.cloneUrl) : "ado";
}

/**
 * The host of the repository named `repo` in the project's config
 * (case-insensitive), or null when the config does not map that name.
 * Repository names are unique per ADO project across hosts (ER-1683 Resolved
 * Decision 4), so the name alone finds the entry. Pure.
 */
export function repoHostByName(
  repos: readonly RepoHostSource[] | null | undefined,
  repo: string | null | undefined
): GroomingRepoHost | null {
  const key = typeof repo === "string" ? repo.trim().toLowerCase() : "";
  if (!key || !repos) return null;
  const entry = repos.find((candidate) => candidate.repo.toLowerCase() === key);
  return entry ? repoHostOf(entry) : null;
}

/** True when any mapped repository is on GitHub (for wording that would otherwise name only Azure DevOps). Pure. */
export function hasGitHubRepo(repos: readonly RepoHostSource[] | null | undefined): boolean {
  return (repos ?? []).some((entry) => repoHostOf(entry) === "github");
}
